/**
 * Dev-only query-string configuration for Volpe's standalone mode.
 *
 * This is the single source of truth for every dev knob. DebugMenuSection,
 * LoadingProcess, and the test loaders read their values from here. The URL
 * is authoritative: the DEV menu serializes the full config into the query
 * string, and this module reads it back on boot. Live knobs that declare a
 * `storageKey` (currently `theme`) also fall back to localStorage so a bare
 * reload keeps the last choice.
 *
 * SAFETY: every consumer is already gated to standalone dev, but `readDevConfig`
 * also returns defaults-only (and never touches the URL) unless we're in a local
 * build, an E2E standalone run, or a staging build, so importing this from a
 * shared path can never change production behaviour.
 */

import testEpubFiles from './Epub/testEpubFiles.js';

// Full dev context: local build or E2E standalone run. Here the whole dev menu
// and the standalone test-content loaders are available.
const isDevEnvironment = () => {
    try {
        if (typeof window !== 'undefined' && window.__E2E_STANDALONE_MODE__) {
            return true;
        }
    } catch {
        // window not available (fall through to env check).
    }
    return import.meta.env.VITE_ENV === 'local';
};

// Whether readDevConfig is allowed to read the URL. True in full dev AND in
// staging, so the staging-safe reload knobs (e.g. the EPUB pagebreak overlay)
// pick up their query params. Never true in production.
const shouldReadDevConfig = () => isDevEnvironment() || import.meta.env.VITE_ENV === 'staging';

/**
 * Which dev-menu knobs the current environment exposes:
 *   'full'    — local / E2E: every knob.
 *   'staging' — staging build: only knobs flagged `staging: true` (display and
 *               debug knobs that never touch content type or session semantics).
 *   null      — anywhere else (the menu is hidden entirely).
 */
export const devMenuScope = () => {
    if (isDevEnvironment()) {
        return 'full';
    }
    if (import.meta.env.VITE_ENV === 'staging') {
        return 'staging';
    }
    return null;
};

/**
 * Spec for every dev option. `mode` distinguishes options applied live (via a
 * store mutation, no reload) from those that swap loaded content/flags and so
 * require a reload. `formats` limits an option to relevant file types.
 *
 * `param` is the query-string key. `live` options also persist to localStorage
 * under `storageKey` when present. `allowed` lists the values the menu offers
 * as pills. An option with `acceptsUnlisted` also keeps any other non-empty
 * URL value instead of falling back to its default.
 */
export const DEV_OPTION_SPECS = [
    {
        key: 'fileType',
        param: 'file_type',
        allowed: ['pdf', 'epub', 'audio'],
        // Any other value boots the unsupported screen (see LoadingProcess.determineFileType).
        acceptsUnlisted: true,
        default: 'pdf',
        mode: 'reload',
        formats: 'all',
    },
    {
        key: 'pdfSet',
        param: 'pdf_set',
        allowed: ['default', 'stress-532'],
        default: 'default',
        mode: 'reload',
        formats: ['pdf'],
    },
    {
        key: 'testArticles',
        param: 'test_articles',
        allowed: ['original', 'simple', 'magazine', 'educational', 'mixed', 'stress'],
        default: 'original',
        mode: 'reload',
        formats: ['pdf'],
    },
    {
        key: 'epubSet',
        param: 'epub_set',
        allowed: Object.keys(testEpubFiles),
        default: 'simple',
        mode: 'reload',
        formats: ['epub'],
    },
    {
        key: 'theme',
        param: 'theme',
        allowed: ['system', 'light', 'dark', 'sepia', 'cream', 'cyan', 'high-contrast-light', 'high-contrast-dark'],
        // Dark is the dev default so the reader opens in dark without clicking through settings.
        default: 'dark',
        mode: 'live',
        storageKey: 'setTheme',
        formats: 'all',
        // Display-only: safe to expose in staging.
        staging: true,
    },
    {
        key: 'pdfLayout',
        param: 'layout',
        allowed: ['scroll', 'single', 'double'],
        // Vertical scroll is the dev default to make the scroll-mode work easy to reach.
        default: 'scroll',
        mode: 'live',
        formats: ['pdf'],
        // Display-only: safe to expose in staging.
        staging: true,
    },
    {
        key: 'epubLayout',
        param: 'epub_layout',
        allowed: ['scroll', 'single', 'double'],
        default: 'scroll',
        // Applied live through the reader's `layout` event bus (same path as the
        // settings UI), so it needs no reload and works regardless of environment.
        mode: 'live',
        formats: ['epub'],
        // Display-only: safe to expose in staging.
        staging: true,
    },
    {
        key: 'preview',
        param: 'preview',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        key: 'anonymous',
        param: 'anonymous',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        // The knob stands in for the host's permission where it is readable (local
        // and staging), so the editor can be tried without an editor account.
        // Staging stays safe because the host checks every write against the
        // reader's own permission. Standalone there is no host, so pieces stay
        // in the page.
        key: 'addOnsEditing',
        param: 'addons_editing',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: ['epub', 'pdf'],
        staging: true,
    },
    {
        // Stands in for the host's `initialPanel`, which a dashboard link asks
        // for. Hosted, the host sends it, so the knob stays out of staging.
        key: 'addOnsPanel',
        param: 'addons_panel',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: ['epub', 'pdf'],
    },
    {
        // Rich text editing has no host yet; this is the only way to see it in
        // dev. Gates the note editor's rich input and its embed hosts; the
        // add-on editor is rich regardless of this knob.
        key: 'richText',
        param: 'rich_text',
        allowed: ['0', '1'],
        default: '1',
        mode: 'reload',
        type: 'boolean',
        formats: ['epub', 'pdf'],
    },
    {
        key: 'concurrency',
        param: 'concurrency',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        key: 'concurrencyDebug',
        param: 'concurrency_debug',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        key: 'embedded',
        param: 'embedded',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        key: 'customPreview',
        param: 'custom_preview',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: 'all',
    },
    {
        key: 'epubPagebreaks',
        param: 'pagebreaks',
        allowed: ['0', '1'],
        default: '0',
        mode: 'reload',
        type: 'boolean',
        formats: ['epub'],
        // Debug overlay only: safe to expose in staging.
        staging: true,
    },
];

const SPEC_BY_KEY = Object.fromEntries(DEV_OPTION_SPECS.map(spec => [spec.key, spec]));

const readLocalStorage = key => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const acceptsUrlValue = (spec, value) => spec.allowed.includes(value) || (spec.acceptsUnlisted && value !== '');

/**
 * Resolve one option's raw string value with precedence:
 *   URL param  >  localStorage (live knobs only)  >  default
 * Unknown values warn and fall back, mirroring the existing test loaders.
 */
const resolveRaw = (spec, params) => {
    const fromUrl = params.get(spec.param);
    if (fromUrl !== null) {
        if (acceptsUrlValue(spec, fromUrl)) {
            return fromUrl;
        }
        console.warn(
            `[devConfig] Ignoring unknown "${spec.param}=${fromUrl}". Allowed: ${spec.allowed.join(', ')}. Using "${spec.default}".`
        );
        return spec.default;
    }

    if (spec.mode === 'live' && spec.storageKey) {
        const stored = readLocalStorage(spec.storageKey);
        if (stored !== null && spec.allowed.includes(stored)) {
            return stored;
        }
    }

    return spec.default;
};

const toValue = (spec, raw) => (spec.type === 'boolean' ? raw === '1' : raw);

const buildDefaults = () => Object.fromEntries(DEV_OPTION_SPECS.map(spec => [spec.key, toValue(spec, spec.default)]));

/**
 * Read the full dev configuration. Returns a frozen object keyed by spec `key`.
 * In non-dev environments returns defaults only and never reads the URL.
 */
export const readDevConfig = () => {
    if (!shouldReadDevConfig()) {
        return Object.freeze(buildDefaults());
    }

    let params;
    try {
        params = new URLSearchParams(window.location.search);
    } catch {
        return Object.freeze(buildDefaults());
    }

    const config = {};
    for (const spec of DEV_OPTION_SPECS) {
        config[spec.key] = toValue(spec, resolveRaw(spec, params));
    }

    // concurrency_debug only does anything when concurrency is enforced, so make
    // it imply concurrency (matching SessionCountdown's mount condition).
    if (config.concurrencyDebug) {
        config.concurrency = true;
    }

    // EPUB scroll mode is disabled in preview. Mirror the production downgrade.
    if (config.preview && config.epubLayout === 'scroll') {
        config.epubLayout = 'single';
    }

    return Object.freeze(config);
};

/**
 * The spec for a single option (used by the DEV menu to render rows generically).
 */
export const getDevOptionSpec = key => SPEC_BY_KEY[key];

export default readDevConfig;
